/*
 * Copyright (c) 2024.
 * The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import DatePicker from "react-datepicker";
import clsx from "clsx";
import UiFilterLabel from "./Filter/UiFilterLabel";
import dayjs from "dayjs";
import {useDebouncedCallback} from "use-debounce";
import TableFilterRemoveButton
    from "pages/Front/components/Tanstack/components/Filters/components/TableFilterRemoveButton";
import Dates from "utils/Dates";
import {useTranslation} from "react-i18next";
import {CalendarDaysIcon} from "@heroicons/react/24/solid";

interface UiDatePickerProps {
    label?: string;
    className?: string;
    selectedDay?: string | null;
    onChange: (date: Date) => void;
    format?: string;
    disabled?: boolean;
    disableRemove?: boolean;
}

const UiDatePicker = (props: UiDatePickerProps) => {
    const {t} = useTranslation();
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const debouncedChangeHandler = useDebouncedCallback(data => {
        props.onChange(data);
    }, 300);

    const datePickerHandler = (date): void => {
        setSelectedDate(date);
        const dateSelected = Dates.date(new Date(date), "", "YYYY-MM-DD");
        debouncedChangeHandler(dateSelected);
    };

    const removeFiltersHandler = () => {
        debouncedChangeHandler(null);
    };

    useEffect(() => {
        setSelectedDate(props.selectedDay ? new Date(props.selectedDay) : null);
    }, [props.selectedDay]);

    useEffect(() => {
        console.log("selectedDate", selectedDate && dayjs(Dates.date(String(selectedDate), "", "YYYY-MM-DD"), "YYYY-MM-DD"));

    }, [selectedDate]);

    return (
        <div className={clsx("UiDatePicker flex flex-col w-auto", props.className)}>
            {props.label && (
                <div className={"relative flex flex-row items-center"}>
                    <UiFilterLabel label={props.label} />
                    {!props.disableRemove && (
                        <TableFilterRemoveButton className={"!relative !right-auto mb-2"} inputValue={selectedDate} onClick={removeFiltersHandler} />
                    )}
                </div>
            )}
            <div className={"flex flex-row items-center shadow-sm shadow-black/10 rounded relative group"}>
                <DatePicker
                    allowClear={false}
                    className={clsx(
                        "pr-[4.5rem] focus:bg-white rounded inline-flex items-center pl-3.5 py-2 text-sm font-medium transition duration-150 group-hover:bg-white group-focus-within:bg-white border border-gray-500 placeholder:text-gray-800 focus:outline focus:outline-1 focus:outline-blue-400 focus:-outline-offset-1",
                        selectedDate ? "text-blue-400" : "text-dark-800 bg-gray-100",
                    )}
                    disabled={props.disabled}
                    format={props.format ? props.format : "dddd D.M.YYYY"}
                    style={{
                        borderWidth: 0,
                        width: 217,
                    }}
                    selected={selectedDate}
                    onChange={datePickerHandler}
                    placeholderText={t("Select date")}
                />
                {selectedDate && (
                    <TableFilterRemoveButton className={"!right-11 border-r pr-1.5 z-10"} inputValue={selectedDate} onClick={removeFiltersHandler} />
                )}
                <div
                    className={
                        "group-hover:text-blue-400  absolute pr-3.5 pl-3.5 right-0 top-0 bottom-0 flex items-center justify-center rounded-tr rounded-br w-auto transition duration-150 hover:outline-1"
                    }
                >
                    <CalendarDaysIcon className={"w-4 h-4 flex-none z-10 relative"} />
                </div>
            </div>
        </div>
    );
};

export default UiDatePicker;
